/**
 * Team room → Players tab.
 *
 * A clean, read-first roster of the players in the CURRENT workspace. It
 * borrows Player Management's visual language (DM Sans, 18px names, status
 * pills, #E4E7EC borders) but NOT its functionality — no workspace
 * reassignment, role toggle, archive/delete or billing. Those live in
 * Player Management. Here you just see this workspace's crew.
 *
 * Data is real:
 *   - players: UseCompanyWorkspaceUsers(workspaceId) filters the company
 *     roster by user.groupId.
 *   - points: UseTopPlayers("month") — the SAME workspace-scoped source the
 *     leaderboard uses (userId → scores.total). refreshKpis() loads it.
 *
 * Search is controlled by the parent so it can sit in the page toolbar.
 */
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "@mui/material/useMediaQuery";

import moment from "moment";

import SoftBox from "../../components/SoftBox";
import { UseCompanyWorkspaceUsers } from "../../hooks/company";
import { refreshKpis, getPreviousKpis } from "../../redux/kpi/actions";

const FONT = "DM Sans, Inter, sans-serif";

const STATUS_STYLES = {
  Active: { bg: "#ECFDF3", color: "#067647", dot: "#12B76A" },
  Pending: { bg: "#FEF6EE", color: "#B93815", dot: "#F38744" },
};

const fullName = (p) => `${p?.firstName || ""} ${p?.lastName || ""}`.trim() || "Unnamed player";

const statusOf = (p) => (p?.logins > 0 ? "Active" : "Pending");

export default function PlayersTab({ workspaceId, search = "" }) {
  const dispatch = useDispatch();
  const players = UseCompanyWorkspaceUsers(workspaceId);
  // The desktop layout is 4 fixed-width columns beside a flexible name
  // column — on a phone the 3 fixed columns (160+90+120px) alone are wider
  // than the whole card, so the name column gets squeezed to ~0 and its
  // text renders on top of/under the neighboring columns. Mobile gets its
  // own stacked layout instead of fighting for the same horizontal space.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Points come from the score store (same data the leaderboard uses). The
  // live buckets are THIS calendar month, which is ~0 at the start of a month —
  // so we also load the previous completed month (the period the leaderboard
  // itself defaults to) and show whichever period actually has points.
  const prevMonth = useMemo(() => moment().subtract(1, "months"), []);
  const prevRef = prevMonth.format("YYYY-MM");
  const liveKpis = useSelector((s) => s.kpiReducer?.usersKpis);
  const prevKpis = useSelector((s) => s.kpiReducer?.historicKpis?.[prevRef]);

  // Nothing else on this route loads scores, so pull both periods for the
  // current workspace on open and whenever the workspace changes.
  useEffect(() => {
    dispatch(refreshKpis());
    dispatch(getPreviousKpis(prevMonth));
  }, [dispatch, workspaceId, prevMonth]);

  const buildTotals = (arr) => {
    const map = {};
    (Array.isArray(arr) ? arr : []).forEach((u) => {
      const total = u?.month?.total;
      if (u?.userId != null && typeof total === "number") map[String(u.userId)] = total;
    });
    return map;
  };
  const liveMap = useMemo(() => buildTotals(liveKpis), [liveKpis]);
  const prevMap = useMemo(() => buildTotals(prevKpis), [prevKpis]);

  const pointsFor = (p) => {
    const ids = [String(p?.userId), String(p?._id)];
    const pick = (mp) => ids.map((id) => mp[id]).find((v) => typeof v === "number");
    const cur = pick(liveMap);
    const prev = pick(prevMap);
    if (typeof cur === "number" && cur > 0) return cur;
    if (typeof prev === "number" && prev > 0) return prev;
    return cur ?? prev;
  };

  const roster = useMemo(() => {
    const key = search.trim().toLowerCase();
    return (players || [])
      .filter((p) => !p?.isArchived)
      .filter((p) => {
        if (!key) return true;
        return (
          fullName(p).toLowerCase().includes(key) ||
          (p?.email || "").toLowerCase().includes(key) ||
          (p?.team || "").toLowerCase().includes(key)
        );
      })
      .sort((a, b) => (pointsFor(b) ?? -1) - (pointsFor(a) ?? -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, search, liveMap, prevMap]);

  const headCell = { fontFamily: FONT, fontSize: "12px", fontWeight: 600, color: "#667085" };

  return (
    <SoftBox sx={{ background: "#FFF", border: "1px solid #E4E7EC", borderRadius: "12px", padding: "4px 20px 12px", fontFamily: FONT }}>
      {isMobile ? (
        <SoftBox sx={{ padding: "14px 0 12px", borderBottom: "1px solid #EAECF0" }}>
          <SoftBox sx={headCell}>Players</SoftBox>
        </SoftBox>
      ) : (
        <SoftBox sx={{ display: "flex", alignItems: "center", padding: "14px 0 12px", borderBottom: "1px solid #EAECF0" }}>
          <SoftBox sx={{ ...headCell, flex: 1 }}>Player</SoftBox>
          <SoftBox sx={{ ...headCell, width: "160px" }}>Team</SoftBox>
          <SoftBox sx={{ ...headCell, width: "90px", textAlign: "center" }}>Points</SoftBox>
          <SoftBox sx={{ ...headCell, width: "120px", textAlign: "center" }}>Status</SoftBox>
        </SoftBox>
      )}

      {roster.length === 0 ? (
        <SoftBox sx={{ padding: "48px 0", textAlign: "center", fontFamily: FONT, fontSize: "14px", color: "#667085" }}>
          {(players || []).filter((p) => !p?.isArchived).length === 0
            ? "No players in this workspace yet."
            : "No players match your search."}
        </SoftBox>
      ) : (
        roster.map((p, i) => {
          const pts = pointsFor(p);
          const status = statusOf(p);
          const s = STATUS_STYLES[status] || STATUS_STYLES.Pending;
          const statusPill = (
            <SoftBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: s.bg,
                color: s.color,
                padding: "3px 10px",
                fontFamily: FONT,
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "999px",
                whiteSpace: "nowrap",
              }}
            >
              <SoftBox component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
              {status}
            </SoftBox>
          );

          if (isMobile) {
            return (
              <SoftBox
                key={p.userId || p._id || i}
                sx={{ padding: "14px 0", borderBottom: i === roster.length - 1 ? 0 : "1px solid #F2F4F7" }}
              >
                <SoftBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                  <SoftBox sx={{ minWidth: 0 }}>
                    <SoftBox sx={{ fontFamily: FONT, fontSize: "16px", fontWeight: 500, color: "#191D23" }}>
                      {fullName(p)}
                    </SoftBox>
                    {p?.email ? (
                      <SoftBox sx={{ fontFamily: FONT, fontSize: "12px", color: "#98A2B3", marginTop: "2px" }}>
                        {p.email}
                      </SoftBox>
                    ) : null}
                  </SoftBox>
                  <SoftBox sx={{ flexShrink: 0 }}>{statusPill}</SoftBox>
                </SoftBox>

                <SoftBox sx={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px", fontFamily: FONT, fontSize: "12.5px" }}>
                  <SoftBox sx={{ color: p?.team ? "#475467" : "#98A2B3", fontWeight: 500 }}>
                    {p?.team || "No team"}
                  </SoftBox>
                  <SoftBox sx={{ color: "#D0D5DD" }}>•</SoftBox>
                  <SoftBox sx={{ color: "#191D23", fontWeight: 600 }}>
                    {typeof pts === "number" ? `${Math.round(pts).toLocaleString()} pts` : "— pts"}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            );
          }

          return (
            <SoftBox
              key={p.userId || p._id || i}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "14px 0",
                borderBottom: i === roster.length - 1 ? 0 : "1px solid #F2F4F7",
              }}
            >
              <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                <SoftBox sx={{ fontFamily: FONT, fontSize: "18px", fontWeight: 500, color: "#191D23" }}>
                  {fullName(p)}
                </SoftBox>
                {p?.email ? (
                  <SoftBox sx={{ fontFamily: FONT, fontSize: "12.5px", color: "#98A2B3", marginTop: "2px" }}>
                    {p.email}
                  </SoftBox>
                ) : null}
              </SoftBox>

              <SoftBox sx={{ width: "160px", fontFamily: FONT, fontSize: "13px", fontWeight: 500, color: p?.team ? "#475467" : "#98A2B3" }}>
                {p?.team || "—"}
              </SoftBox>

              <SoftBox sx={{ width: "90px", textAlign: "center", fontFamily: FONT, fontSize: "16px", fontWeight: 600, color: "#191D23" }}>
                {typeof pts === "number" ? Math.round(pts).toLocaleString() : "—"}
              </SoftBox>

              <SoftBox sx={{ width: "120px", display: "flex", justifyContent: "center" }}>
                {statusPill}
              </SoftBox>
            </SoftBox>
          );
        })
      )}
    </SoftBox>
  );
}
